// SideBanners.js
// Renders left and right vertical banners for desktop layout
// Configure in: src/config/sideBannersConfig.js

import React, { useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import {
  sideBannersConfig,
  shouldShowSideBannersOnPage,
  getMinScreenWidth,
} from '../config/sideBannersConfig';
import '../styles/SideBanners.css';

const SideBanner = ({ config, position, isAuthenticated }) => {
  if (!config.enabled) {
    return null;
  }

  // Determine the link based on auth status
  // If link is 'dynamic', use auth-based routing
  const getLink = () => {
    if (config.link === 'dynamic') {
      return isAuthenticated ? '/post-create' : '/register';
    }
    return config.link;
  };

  const link = getLink();

  // Determine if this is an image banner
  const isImageBanner = config.type === 'image' && config.imageSrc;

  // Image banner content
  const imageContent = (
    <img
      src={config.imageSrc}
      alt={config.imageAlt || 'Banner'}
      className="side-banner__image"
    />
  );

  // Text banner content
  const textContent = (
    <>
      {config.icon && (
        <div className="side-banner__icon">{config.icon}</div>
      )}
      <div className="side-banner__content">
        <h3 className="side-banner__title">{config.title}</h3>
        {config.description && (
          <p className="side-banner__description">{config.description}</p>
        )}
      </div>
      {config.link && config.linkText && (
        <div className="side-banner__action">
          {config.link.startsWith('http') ? (
            <a
              href={config.link}
              target="_blank"
              rel="noopener noreferrer"
              className="side-banner__link"
            >
              {config.linkText}
            </a>
          ) : (
            <Link to={config.link} className="side-banner__link">
              {config.linkText}
            </Link>
          )}
        </div>
      )}
    </>
  );

  const content = isImageBanner ? imageContent : textContent;
  
  const bannerClasses = [
    'side-banner',
    `side-banner--${position}`,
    isImageBanner ? 'side-banner--image' : '',
  ].filter(Boolean).join(' ');
  
  const bannerStyle = isImageBanner 
    ? {} 
    : {
        background: config.backgroundColor || '#3ac693',
        color: config.textColor || '#ffffff',
      };

  // If banner has a link, make it clickable
  if (link) {
    const isExternal = link.startsWith('http');
    
    if (isExternal) {
      return (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className={bannerClasses}
          style={bannerStyle}
        >
          {content}
        </a>
      );
    }
    
    return (
      <Link to={link} className={bannerClasses} style={bannerStyle}>
        {content}
      </Link>
    );
  }

  return (
    <div className={bannerClasses} style={bannerStyle}>
      {content}
    </div>
  );
};

const SideBanners = () => {
  const [isWideEnough, setIsWideEnough] = React.useState(false);
  const location = useLocation();
  const { isAuthenticated } = useContext(AuthContext);

  // Check screen width on mount and resize
  React.useEffect(() => {
    const checkWidth = () => {
      const minWidth = getMinScreenWidth();
      setIsWideEnough(window.innerWidth >= minWidth);
    };

    checkWidth();
    window.addEventListener('resize', checkWidth);
    return () => window.removeEventListener('resize', checkWidth);
  }, []);

  // Determine if banners should show
  const shouldShow = 
    sideBannersConfig.enabled && 
    shouldShowSideBannersOnPage(location.pathname) && 
    isWideEnough;

  // Add/remove body class when banners are visible (to adjust panel width)
  React.useEffect(() => {
    if (shouldShow) {
      document.body.classList.add('side-banners-active');
    } else {
      document.body.classList.remove('side-banners-active');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('side-banners-active');
    };
  }, [shouldShow]);

  // Don't render if conditions not met
  if (!shouldShow) return null;

  const { leftBanner, rightBanner } = sideBannersConfig;

  return (
    <>
      <SideBanner config={leftBanner} position="left" isAuthenticated={isAuthenticated} />
      <SideBanner config={rightBanner} position="right" isAuthenticated={isAuthenticated} />
    </>
  );
};

export default SideBanners;
